import React, { useId } from 'react';
import { useSelector } from 'react-redux';
import { Alert, CircularProgress } from '@mui/material';
import { RootState } from '../../../../store/rootReducer';
import { IAsyncOperationState, ITopicTarget } from '../../../../api/realtime/types';
import { activeTopicOperationSelector } from '../../../../store/reducers/asyncOperations/selectors';
import { CircularProgressWithLabel } from '../../../../components/CircularProgressWithLabel/CircularProgressWithLabel';
import { useRecallSyncProgressStyles } from './styles';
import {
  getCompletedMessage,
  getOperationDetails,
  getOperationTitle,
  hasDeterminateProgress,
} from './helpers';
import { CANCELLED_MESSAGE, DEFAULT_FAILED_MESSAGE } from './constants';

type TRecallSyncProgressProps = {
  /** The page whose running operation to show; null while live progress is off */
  target: ITopicTarget | null;
  finishedOperation: IAsyncOperationState | null;
  onDismiss: () => void;
};

/** The progress of the service center's running recall operation, or how the last one ended. */
const RecallSyncProgress: React.FC<TRecallSyncProgressProps> = ({
  target,
  finishedOperation,
  onDismiss,
}) => {
  const { classes } = useRecallSyncProgressStyles();
  // a progress bar needs an accessible name: the visible title of the operation
  const titleId = useId();
  // selected here, so a progress message re-renders this banner and not the whole page
  const activeOperation = useSelector((state: RootState) =>
    activeTopicOperationSelector(state, target)
  );

  if (activeOperation) {
    return (
      <div className={classes.wrapper} role="status">
        {/* until the operation reports progress, a spinning circle shows that it is alive */}
        {hasDeterminateProgress(activeOperation) ? (
          <CircularProgressWithLabel
            value={activeOperation.percent ?? 0}
            aria-labelledby={titleId}
          />
        ) : (
          <CircularProgress aria-labelledby={titleId} />
        )}
        <div className={classes.text}>
          <span id={titleId} className={classes.title}>
            {getOperationTitle(activeOperation)}
          </span>
          <span className={classes.details}>{getOperationDetails(activeOperation)}</span>
        </div>
      </div>
    );
  }

  if (!finishedOperation) return null;

  if (finishedOperation.status === 'Completed') {
    return (
      <Alert className={classes.alert} severity="success">
        {getCompletedMessage(finishedOperation)}
      </Alert>
    );
  }

  return finishedOperation.status === 'Failed' ? (
    <Alert className={classes.alert} severity="error" onClose={onDismiss}>
      {finishedOperation.errorMessage || DEFAULT_FAILED_MESSAGE}
    </Alert>
  ) : (
    <Alert className={classes.alert} severity="info" onClose={onDismiss}>
      {CANCELLED_MESSAGE}
    </Alert>
  );
};

export default RecallSyncProgress;
